import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import type { Snapshot } from '@/api/types/readiness'
import { scoreBand } from '@/lib/readiness/score'
import { savedLine2 } from '@/lib/readiness/compare'
import { LIVE } from '@/lib/readiness/comparePair'
import { BandWord } from '@/components/readiness/ScoreTooltip'
import { Chip, Icon, SectionLabel } from '@/components/readiness/primitives'
import { OriginLink } from '@/lib/readiness/nav'
import { formatDate } from '@/components/common/format'

// ── Assessment rail (I1 §9.4, I2 §7.2, I6 §2.2, I6 fix) ─────────────────────
// Beside Compare assessments: the Live card, ONE Saved assessments card whose
// list scrolls (newest first, fade at the foot, pinned `View all` when it
// overflows) and the Schedule card. The rail is absolutely positioned inside
// its wrapper by the page's CSS, so the card beside it sets the row height.

export function AssessmentRail({ snapshots, scale, liveNumber, unconfirmed, fromId, toId, defaultName, fmt, q, onReviewSave, onSetPair, onViewAll, schedule, extra }: {
  snapshots: Snapshot[]
  scale: 'levels' | 'score_0_10'
  liveNumber: number
  unconfirmed: number
  fromId: string
  toId: string
  defaultName: string
  fmt: (v: number) => string
  q: string
  onReviewSave: () => void
  onSetPair: (from: string, to: string) => void
  onViewAll: () => void
  schedule: ReactNode
  extra?: ReactNode
}) {
  // With nothing saved both ids resolve to Live, which is then the To side.
  const tagFor = (id: string) => (id === toId && (id === LIVE || id !== fromId) ? 'TO' : id === fromId ? 'FROM' : null)
  const newestFirst = [...snapshots].reverse()
  const n = snapshots.length

  // Scroll state of the list: whether it overflows, whether it is at the end,
  // and the scrollbar gutter the fade must leave clear.
  const listRef = useRef<HTMLDivElement>(null)
  const [scroll, setScroll] = useState({ overflow: false, atEnd: true, gutter: 0 })
  const measure = useCallback(() => {
    const el = listRef.current
    if (!el) return
    const overflow = el.scrollHeight > el.clientHeight + 1
    const atEnd = el.scrollTop + el.clientHeight >= el.scrollHeight - 1
    const gutter = el.offsetWidth - el.clientWidth
    setScroll((s) => (s.overflow === overflow && s.atEnd === atEnd && s.gutter === gutter ? s : { overflow, atEnd, gutter }))
  }, [])
  useLayoutEffect(measure, [measure, n])
  useEffect(() => {
    const el = listRef.current
    if (!el) return
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    window.addEventListener('resize', measure)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [measure])

  return (
    <div className="rd-rail-wrap" data-testid="assessment-rail-wrap">
      <aside className="rd-rail" aria-label="Saved assessments" data-testid="assessment-rail">
        {/* a · Live */}
        <div className="shrink-0 rounded-[12px] p-4" style={{ border: '1px dashed var(--rd-border-control)' }} data-testid="rail-live">
          <div className="flex items-center justify-between gap-2">
            <p className="flex min-w-0 items-center gap-2 text-[14px] font-semibold"><span className="truncate">Live · {formatDate(new Date().toISOString())}</span>{tagFor(LIVE) && <span className="rd-pair-tag" data-testid="rail-tag-live">{tagFor(LIVE)}</span>}</p>
            <span className="rd-mono shrink-0 text-[14px] font-semibold">{fmt(liveNumber)}{scale === 'levels' && <> · <BandWord band={scoreBand(liveNumber)} className="text-[12.5px]" /></>}</span>
          </div>
          <p className="mt-1 text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>Not yet saved · {unconfirmed} unconfirmed {unconfirmed === 1 ? (scale === 'levels' ? 'level' : 'score') : scale === 'levels' ? 'levels' : 'scores'}</p>
          <div className="mt-2 flex flex-wrap gap-3 text-[12.5px]">
            <button type="button" className="rd-link" onClick={onReviewSave} data-testid="review-save-link">Review & save as {defaultName} →</button>
            {toId !== LIVE && <button type="button" className="rd-link" onClick={() => onSetPair(fromId === LIVE ? toId : fromId, LIVE)} data-testid="rail-set-to-live">Set as To</button>}
          </div>
        </div>

        {/* b · Saved assessments: one card, a scrolling list */}
        <section className="rd-card rd-rail-saved" aria-labelledby="rd-rail-saved" data-testid="rail-saved">
          <div className="flex shrink-0 items-center justify-between gap-2 px-4 pb-3 pt-4">
            <h3 id="rd-rail-saved" className="flex items-baseline gap-2 text-[14px] font-bold">Saved assessments <span className="rd-mono text-[12.5px] font-normal" style={{ color: 'var(--rd-text-muted)' }} data-testid="rail-saved-count">{n}</span></h3>
            {n > 0 && <button type="button" className="rd-link text-[12.5px]" onClick={onViewAll} data-testid="rail-view-all">View all</button>}
          </div>
          {n === 0 ? (
            <div className="flex flex-1 flex-col items-center justify-center px-6 py-8 text-center" style={{ borderTop: '1px solid var(--rd-border-subtle)' }} data-testid="rail-empty">
              <Icon name="file" size={28} style={{ color: 'var(--rd-text-faint)' }} />
              <p className="mt-3 text-[13.5px] font-semibold">No saved assessments yet</p>
              <p className="mt-1 max-w-[260px] text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>A saved assessment records confirmed levels and evidence as of that day. Your first one becomes the baseline.</p>
            </div>
          ) : (
            <>
              <div className="rd-rail-list-wrap">
                <div ref={listRef} className="rd-rail-list rd-scroll-thin" onScroll={measure} data-testid="rail-list">
                  {newestFirst.map((s) => {
                    const tag = tagFor(s.id)
                    return (
                      <div key={s.id} className={`px-4 py-3 ${tag === 'FROM' ? 'rd-rail-row-from' : ''}`} style={{ borderTop: '1px solid var(--rd-border-subtle)' }} data-testid={`assessment-${s.id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <p className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[13.5px] font-semibold leading-[1.3]"><span>{s.name}</span>{s.baseline && <Chip tone="chip" className="rd-chip-sm">BASELINE</Chip>}{tag && <span className="rd-pair-tag" data-testid={`rail-tag-${s.id}`}>{tag}</span>}</p>
                          <span className="rd-mono rd-id shrink-0 whitespace-nowrap text-[13.5px] font-semibold">{fmt(s.score)}{scale === 'levels' && <> · <BandWord band={scoreBand(s.score)} className="text-[12px]" /></>}</span>
                        </div>
                        <p className="mt-0.5 text-[12px]" style={{ color: 'var(--rd-text-muted)' }}>{savedLine2(s)}</p>
                        {s.note && <p className="rd-rail-note mt-0.5 text-[12px]" title={s.note}>{s.note}</p>}
                        <div className="mt-1.5 flex flex-wrap gap-3 text-[12.5px]">
                          {/* View opens the saved assessment's report with `← Progress` (I5 §12.1). */}
                          <OriginLink to={`/readiness/report/${s.id}${q}`} className="rd-link" data-testid={`assessment-view-${s.id}`}>View</OriginLink>
                          <OriginLink to={`/readiness/report/${s.id}${q}&print=1`} className="rd-link" originKey={`export-${s.id}`}>Export PDF</OriginLink>
                          {tag !== 'FROM' && <button type="button" className="rd-link" onClick={() => onSetPair(s.id, toId === s.id ? LIVE : toId)} data-testid={`set-from-${s.id}`}>Set as From</button>}
                        </div>
                      </div>
                    )
                  })}
                </div>
                {scroll.overflow && !scroll.atEnd && <div className="rd-rail-fade" style={{ right: scroll.gutter }} aria-hidden="true" data-testid="rail-fade" />}
              </div>
              {scroll.overflow && (
                <div className="shrink-0 px-4 py-2.5 text-[12.5px]" style={{ borderTop: '1px solid var(--rd-border-subtle)' }}>
                  <button type="button" className="rd-link" onClick={onViewAll} data-testid="rail-view-all-footer">View all {n} assessments →</button>
                </div>
              )}
            </>
          )}
        </section>

        {/* c · Schedule (unchanged) */}
        <div className="rd-card shrink-0 p-4" data-testid="rail-schedule">
          <SectionLabel>Schedule</SectionLabel>
          {schedule}
        </div>
        {extra}
      </aside>
    </div>
  )
}
